import {NavigationBarItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  GenericIconName,
  Icon,
  UnstyledButton,
  Z_INDEXES,
  useSlider,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useRef} from 'react';
import {useRect} from 'react-viewport-utils';
import {NAVIGATION_HEIGHT, SEARCH_BAR_TABLET_WIDTH} from '../../constants';
import {useShouldDisplaySlider} from '../hooks/useShouldDisplaySlider';
import {CategoryNavigationItem} from './CategoryNavigationItem';

export type CategoryItem = NavigationBarItem & {
  icon?: {
    variant: 'sale' | 'coupon';
    color: string;
    foregroundColor?: string;
    ['data-testid']: string;
  };
};

interface CategoryNavigationItemsProps {
  activeNavItemIndex: number | undefined;
  items: CategoryItem[];
  onEnterNavigationItem: (index: number) => () => void;
}

export const CategoryNavigationItems: React.FC<
  CategoryNavigationItemsProps & {isSearchBarExpanded: boolean}
> = ({
  activeNavItemIndex,
  items,
  onEnterNavigationItem,
  isSearchBarExpanded,
}) => {
  const {t} = useTranslation();
  const navItemsWrapperRef = useRef(null);
  const navItemsWrapperRect = useRect(navItemsWrapperRef, {
    disableScrollUpdates: true,
    priority: 'low',
  });
  const shouldDisplaySlider = useShouldDisplaySlider(
    navItemsWrapperRect,
    isSearchBarExpanded,
  );

  const scrollWrapperRef = React.useRef<HTMLDivElement>(null);
  const {next, prev, nextIsActive, prevIsActive} = useSlider(scrollWrapperRef);

  return (
    <Wrapper data-testid="topCategoryNavigationItems">
      <PreviousButton
        data-testid="topCategoryNavigationItems-prev-btn"
        hidden={!prevIsActive || !shouldDisplaySlider}
        onClick={() => prevIsActive && prev()}
        aria-label={t('Navigate back')}
      >
        <Icon size="S" asset={GenericIconName.ArrowLeft} />
      </PreviousButton>
      <ScrollableWrapper ref={scrollWrapperRef}>
        <NavItemsWithRef
          activeNavItemIndex={activeNavItemIndex}
          items={items}
          onEnterNavigationItem={onEnterNavigationItem}
          ref={navItemsWrapperRef}
        />
      </ScrollableWrapper>
      <NextButton
        data-testid="topCategoryNavigationItems-next-btn"
        hidden={!nextIsActive || !shouldDisplaySlider}
        onClick={() => nextIsActive && next()}
        aria-label={t('Navigate forth')}
      >
        <Icon size="S" asset={GenericIconName.ArrowRight} />
      </NextButton>
    </Wrapper>
  );
};

const NavItemsWithRef = ({
  activeNavItemIndex,
  items,
  onEnterNavigationItem,
  ref,
}: CategoryNavigationItemsProps & {ref?: React.Ref<HTMLUListElement>}) => {
  return (
    <WrapperInner ref={ref} data-testid="topCategoriesContainer">
      {items.map(
        (
          {badges, label, url: linkTarget, textColor, category, icon},
          index,
        ) => (
          <CategoryNavigationItem
            category={category}
            icon={icon}
            badges={badges}
            isActive={index === activeNavItemIndex}
            itemHasContent={!!items[index]?.flyout}
            key={`${label}-${index}`}
            label={label}
            onEnterNavigationItem={onEnterNavigationItem(index)}
            textColor={textColor}
            url={linkTarget}
          />
        ),
      )}
    </WrapperInner>
  );
};

const Wrapper = styled.div`
  position: relative;
  left: 0;
  max-width: calc(100% - ${SEARCH_BAR_TABLET_WIDTH}px);
  height: 100%;
`;

const ButtonBase = styled(UnstyledButton)`
  position: absolute;
  top: 0;
  background: ${COLORS.White} !important;
  width: 32px;
  height: 100%;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.16);
  z-index: ${Z_INDEXES.TADARIDA_HEADER_NAVIGATION_BUTTONS};
  & > svg {
    position: absolute;
    top: ${NAVIGATION_HEIGHT / 2 - 13}px;
    left: 5px;
  }
`;

const PreviousButton = styled(ButtonBase)`
  left: -20px;
`;

const NextButton = styled(ButtonBase)`
  right: -1px;
`;

const ScrollableWrapper = styled.div`
  overflow-x: scroll;
  overflow-y: hidden;
  margin: auto;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const WrapperInner = styled.ul`
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin: auto;
  height: 100%;
`;
